'use client';

import React, { useState } from 'react';
import { SliderInputComponent } from '../types/content-components.types.js';
import { Target } from 'lucide-react';

interface SliderInputProps {
  component: SliderInputComponent;
}

export const SliderInput: React.FC<SliderInputProps> = ({ component }) => {
  const { props } = component;
  const [value, setValue] = useState(Math.floor((props.min + props.max) / 2));
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleSubmit = () => {
    setHasSubmitted(true);
  };

  const getResultMessage = () => {
    if (!props.correctValue) return null;
    
    const diff = Math.abs(value - props.correctValue);
    const range = props.max - props.min;
    const percentOff = (diff / range) * 100;

    if (percentOff < 10) return { text: '🎯 Very close!', color: 'text-green-700' };
    if (percentOff < 25) return { text: '👍 Pretty good!', color: 'text-green-600' };
    if (percentOff < 50) return { text: '🤔 Not bad', color: 'text-learn-honey-700' };
    return { text: '💭 Interesting guess', color: 'text-gray-700' };
  };

  const result = hasSubmitted ? getResultMessage() : null;

  return (
    <div className="w-full">
      <div className="bg-learn-sky-50 border border-learn-sky-200 rounded-xl p-5">
        {/* Question */}
        <div className="mb-6">
          <h4 className="text-base font-semibold text-learn-sky-900 mb-2 flex items-center gap-2">
            <Target className="w-5 h-5" />
            Your Estimate
          </h4>
          <p className="text-sm text-gray-800 leading-relaxed">
            {props.question}
          </p>
        </div>

        {/* Current Value Display */}
        <div className="text-center mb-4">
          <div className="inline-block bg-white rounded-lg px-6 py-3 shadow-sm border border-learn-sky-200">
            <span className="text-3xl font-bold text-learn-sky-900">
              {value}
              {props.unit && <span className="text-xl text-gray-600 ml-1">{props.unit}</span>}
            </span>
          </div>
        </div>

        {/* Slider */}
        <div className="mb-6">
          <input
            type="range"
            min={props.min}
            max={props.max}
            value={value}
            onChange={(e) => !hasSubmitted && setValue(Number(e.target.value))}
            disabled={hasSubmitted}
            className="w-full h-2 bg-learn-sky-200 rounded-lg appearance-none cursor-pointer slider-thumb"
            style={{
              background: `linear-gradient(to right, #6B9AB3 0%, #6B9AB3 ${((value - props.min) / (props.max - props.min)) * 100}%, #CFE0E9 ${((value - props.min) / (props.max - props.min)) * 100}%, #CFE0E9 100%)`
            }}
          />
          
          {/* Labels */}
          {props.labels && (
            <div className="flex justify-between mt-2">
              <span className="text-xs text-gray-600">{props.labels.min}</span>
              <span className="text-xs text-gray-600">{props.labels.max}</span>
            </div>
          )}
        </div>

        {/* Submit Button */}
        {!hasSubmitted ? (
          <button
            onClick={handleSubmit}
            className="w-full px-4 py-2 bg-learn-sky-500 text-white rounded-lg font-medium hover:bg-learn-sky-600 transition-colors shadow-sm"
          >
            Submit Estimate
          </button>
        ) : (
          <>
            {/* Result */}
            {result && (
              <div className="bg-white rounded-lg p-4 border border-learn-sky-200 mb-3">
                <p className={`text-sm font-semibold ${result.color} mb-2`}>
                  {result.text}
                </p>
                {props.correctValue !== undefined && (
                  <p className="text-sm text-gray-700">
                    The answer is <span className="font-semibold">{props.correctValue}{props.unit && ` ${props.unit}`}</span>
                    {value !== props.correctValue && ` (you guessed ${value}${props.unit ? ` ${props.unit}` : ''})`}
                  </p>
                )}
              </div>
            )}

            {/* Explanation */}
            {props.explanation && (
              <div className="bg-white rounded-lg p-4 border border-learn-sky-200">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {props.explanation}
                </p>
              </div>
            )}

            {/* Reset */}
            <button
              onClick={() => {
                setHasSubmitted(false);
                setValue(Math.floor((props.min + props.max) / 2));
              }}
              className="mt-3 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2"
            >
              Try Different Value
            </button>
          </>
        )}
      </div>
    </div>
  );
};

